import { useMemo, useState } from 'react';
import {
  addMonths,
  budgetStatus,
  categoryBreakdown,
  compareToPreviousMonth,
  dailyAverageSen,
  dailyTotals,
  formatDayLabel,
  formatMonthLabel,
  formatMoney,
  inRange,
  largestExpenses,
  monthKey as monthKeyOf,
  monthRange,
  monthlyTrend,
  recurringMerchants,
} from '@wis/shared';
import { useApp } from '../state/AppContext.js';
import { Button, EmptyState, Money } from '../components/primitives.js';
import {
  CategoryBreakdown,
  CategoryTable,
  DailySpark,
  MonthlyTrendChart,
} from '../components/charts.js';

export function Insights(): JSX.Element {
  const { transactions, categories, settings } = useApp();
  const timeZone = settings.timeZone;
  const now = useMemo(() => new Date(), []);
  const currentMonth = monthKeyOf(now, timeZone);

  const [month, setMonth] = useState(currentMonth);
  const [showTable, setShowTable] = useState(false);

  const monthTransactions = useMemo(
    () => inRange(transactions, monthRange(month, timeZone)),
    [transactions, month, timeZone],
  );

  const breakdown = useMemo(
    () => categoryBreakdown(monthTransactions, categories),
    [monthTransactions, categories],
  );

  const trend = useMemo(
    () => monthlyTrend(transactions, month, 6, { timeZone, categories }),
    [transactions, month, timeZone, categories],
  );

  const daily = useMemo(
    () => dailyTotals(transactions, month, { timeZone, categories }),
    [transactions, month, timeZone, categories],
  );

  const average = useMemo(
    () => dailyAverageSen(transactions, month, { now, timeZone, categories }),
    [transactions, month, now, timeZone, categories],
  );

  const comparison = useMemo(
    () => compareToPreviousMonth(transactions, month, { timeZone, categories }),
    [transactions, month, timeZone, categories],
  );

  const largest = useMemo(
    () => largestExpenses(monthTransactions, categories, 5),
    [monthTransactions, categories],
  );

  const recurring = useMemo(
    () => recurringMerchants(transactions, categories, 3, 6),
    [transactions, categories],
  );

  const budget = useMemo(
    () =>
      budgetStatus(transactions, settings.monthlyBudgetSen, {
        now: month === currentMonth ? now : new Date(monthRange(month, timeZone).endIso),
        timeZone,
        categories,
      }),
    [transactions, settings.monthlyBudgetSen, month, currentMonth, now, timeZone, categories],
  );

  const toneFor = (categoryId: string): string =>
    categories.find((category) => category.id === categoryId)?.tone ?? 'slate';

  const hasData = transactions.length > 0;

  if (!hasData) {
    return (
      <div style={{ marginTop: 'var(--space-6)' }}>
        <EmptyState
          glyph="📊"
          title="Nothing to chart yet"
          body="Once you have recorded a few payments, this screen shows where the money went, how this month compares with the last, and which merchants keep coming back."
        />
      </div>
    );
  }

  return (
    <div className="stack-lg" style={{ marginTop: 'var(--space-3)' }}>
      <div className="row row--between">
        <Button
          variant="secondary"
          onClick={() => setMonth((current) => addMonths(current, -1))}
          aria-label="Previous month"
        >
          ←
        </Button>
        <h2 className="section__title" aria-live="polite">
          {formatMonthLabel(month, timeZone)}
        </h2>
        <Button
          variant="secondary"
          onClick={() => setMonth((current) => addMonths(current, 1))}
          disabled={month >= currentMonth}
          aria-label="Next month"
        >
          →
        </Button>
      </div>

      <section className="card" aria-labelledby="month-total-heading">
        <h3 className="stat__label" id="month-total-heading">
          Spent in {formatMonthLabel(month, timeZone)}
        </h3>
        <p style={{ marginTop: 'var(--space-1)' }}>
          <Money sen={comparison.currentSen} size="display" currency={settings.currency} />
        </p>
        <p className="small muted">
          {comparison.previousSen === 0
            ? 'Nothing recorded the month before, so there is nothing to compare with yet.'
            : `${formatMoney(Math.abs(comparison.differenceSen))} ${
                comparison.direction === 'up' ? 'more' : comparison.direction === 'down' ? 'less' : 'the same as'
              }${comparison.percentChange !== null ? ` (${Math.abs(comparison.percentChange)}%)` : ''} than ${formatMonthLabel(addMonths(month, -1), timeZone)}.`}
        </p>

        <div className="stat-row">
          <div className="stat">
            <p className="stat__label">Daily average</p>
            <p className="stat__value">
              <Money sen={average} size="large" currency={settings.currency} />
            </p>
            <p className="stat__hint">
              {month === currentMonth ? 'per day so far' : 'per day across the month'}
            </p>
          </div>
          <div className="stat">
            <p className="stat__label">Payments</p>
            <p className="stat__value money money--large">{monthTransactions.length}</p>
            <p className="stat__hint">
              {breakdown.length} {breakdown.length === 1 ? 'category' : 'categories'}
            </p>
          </div>
        </div>
      </section>

      {settings.monthlyBudgetSen > 0 ? (
        <section className="card" aria-labelledby="budget-progress-heading">
          <h3 className="section__title" id="budget-progress-heading">
            Budget progress
          </h3>
          <div
            className="budget__track"
            style={{ marginTop: 'var(--space-3)' }}
            role="img"
            aria-label={`${budget.percentUsed}% of the ${formatMoney(budget.budgetSen)} budget used.`}
          >
            <div
              className="budget__fill"
              style={{ width: `${budget.percentUsed}%` }}
              data-over={budget.isOver}
            />
          </div>
          <p className="chart-summary">
            {formatMoney(budget.spentSen)} of {formatMoney(budget.budgetSen)} used ({budget.percentUsed}%).{' '}
            {budget.isOver
              ? `Over by ${formatMoney(Math.abs(budget.remainingSen))}.`
              : `${formatMoney(budget.remainingSen)} left.`}
          </p>
        </section>
      ) : null}

      <section className="card" aria-labelledby="trend-heading">
        <h3 className="section__title" id="trend-heading">
          Last six months
        </h3>
        <MonthlyTrendChart data={trend} highlightMonth={month} timeZone={timeZone} />
      </section>

      <section className="card" aria-labelledby="daily-heading">
        <h3 className="section__title" id="daily-heading">
          Day by day
        </h3>
        <DailySpark data={daily} average={average} />
      </section>

      <section className="card" aria-labelledby="categories-heading">
        <div className="row row--between">
          <h3 className="section__title" id="categories-heading">
            Where it went
          </h3>
          <button
            type="button"
            className="section__action"
            aria-pressed={showTable}
            onClick={() => setShowTable((current) => !current)}
          >
            {showTable ? 'Show bars' : 'Show table'}
          </button>
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {showTable ? (
            <CategoryTable data={breakdown} />
          ) : (
            <CategoryBreakdown data={breakdown} toneFor={toneFor} showAll />
          )}
        </div>
      </section>

      <section className="card" aria-labelledby="largest-heading">
        <h3 className="section__title" id="largest-heading">
          Largest this month
        </h3>
        {largest.length === 0 ? (
          <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
            No spending recorded in this month.
          </p>
        ) : (
          <ul className="stack" style={{ marginTop: 'var(--space-3)' }}>
            {largest.map((transaction) => (
              <li key={transaction.id} className="row row--between">
                <span className="grow">
                  <span className="tx-row__merchant">
                    {transaction.merchantNormalized || 'No merchant'}
                  </span>
                  <span className="tiny faint" style={{ display: 'block' }}>
                    {formatDayLabel(transaction.occurredAt, timeZone)}
                  </span>
                </span>
                <Money sen={transaction.amountSen} size="medium" currency={transaction.currency} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card" aria-labelledby="recurring-heading">
        <h3 className="section__title" id="recurring-heading">
          Keeps coming back
        </h3>
        {recurring.length === 0 ? (
          <p className="small muted" style={{ marginTop: 'var(--space-2)' }}>
            Nothing has appeared three times or more yet.
          </p>
        ) : (
          <ul className="stack" style={{ marginTop: 'var(--space-3)' }}>
            {recurring.map((entry) => (
              <li key={entry.merchant} className="row row--between">
                <span className="grow">
                  <span className="tx-row__merchant">{entry.merchant}</span>
                  <span className="tiny faint" style={{ display: 'block' }}>
                    {entry.count} times
                    {entry.medianGapDays !== null ? ` · about every ${entry.medianGapDays} days` : ''}
                    {entry.medianGapDays !== null && entry.medianGapDays >= 27 && entry.medianGapDays <= 32
                      ? ' · looks monthly'
                      : ''}
                  </span>
                </span>
                <span>
                  <Money sen={entry.totalSen} size="medium" currency={settings.currency} />
                  <span className="tiny faint" style={{ display: 'block', textAlign: 'right' }}>
                    {formatMoney(entry.averageSen)} each
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
